// src/services/mediaUpload.js
//
// 🟢 WEB MEDIA SEND — the browser twin of ChatModal's image/video/album uploader.
//
// The contract is fixed by the phone, so this file follows it exactly rather than
// inventing a web-only shape (which is the whole reason a web-sent photo has to
// open on an iPhone):
//
//   1. bytes → nacl.secretbox(key, nonce)                    [services/CryptoService.encryptImage]
//   2. ciphertext → POST /api/r2/presign-upload → PUT        [services/MediaStorage.uploadToR2]
//   3. {"k","n"} JSON → sealed per-device with nacl.box      [CryptoService.sealImageKeyForOneOnOne]
//   4. Firestore doc: imageUrl + imagePerDeviceKeys + senderDeviceId
//                     videoUrl + videoPerDeviceKeys + senderDeviceId
//                     images[] + batchPerDeviceKeys (payload is an ARRAY of {k,n,type})
//   5. socket 'new-message' with the same fields → server relays + pushes FCM
//   6. chat-doc write in the PHONE's shape (timestamp / lastMessageId / lastMessage*)
//
// Step 5 is what makes it arrive instantly and ring the recipient's phone; step 6 is
// what makes ChatsTab sort and tick correctly (see the note in messages.js).
//
// ⚠️ R2 BUCKET CORS is required for the browser leg — a phone PUTs from native code
//    where CORS doesn't exist, a browser does not. See R2_SETUP.md §6.

import { sealMediaMeta, withSealedMeta } from '../crypto/mediaMeta';   // 🔐 caption + thumbnails under the media's key
import nacl from 'tweetnacl';
import util from 'tweetnacl-util';
import {
  collection, doc, setDoc, serverTimestamp, deleteField, increment, getDoc,
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { sealForUsers } from '../crypto/e2ee';
import { relayNewMessage, BACKEND_URL } from './socket';
import { sealMembers } from './groupMembers';
import { setMessageDoc } from './sendDoc'; // 🖥📱 origin + my other devices' copy

// ── 1. symmetric encryption (identical primitive + base64 as the phone) ──
export function encryptBytes(u8) {
  const k = nacl.randomBytes(nacl.secretbox.keyLength);
  const n = nacl.randomBytes(nacl.secretbox.nonceLength);
  const cipher = nacl.secretbox(u8, n, k);
  if (!cipher) throw new Error('encryption failed');
  return { cipher, k: util.encodeBase64(k), n: util.encodeBase64(n) };
}

// ── 2. presign + PUT ──
async function presign() {
  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) throw new Error('not signed in');
  const res = await fetch(`${BACKEND_URL}/api/r2/presign-upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  if (!res.ok) throw new Error(`presign failed (${res.status})`);
  const j = await res.json();
  if (!j?.uploadUrl || !j?.publicUrl) throw new Error('bad presign response');
  return j;
}

/** PUT the ciphertext straight to R2. XHR (not fetch) because we want real upload
 *  progress — fetch still has no upload-progress event in any shipping browser. */
function putBytes(uploadUrl, bytes, onProgress, registerAbort) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    // MUST match the ContentType the server signed with, or R2 rejects the PUT.
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    // 🟥 EXACT-LENGTH BODY. nacl.secretbox returns `c.subarray(16)` — a VIEW with a
    //    non-zero byteOffset over a bigger buffer. Every compliant browser sends just
    //    the view, but a body whose length and backing buffer disagree is precisely the
    //    shape that produces a stored object whose Content-Length does not match its
    //    bytes — and a recipient that downloads to ~40% and then stalls for ever waiting
    //    for the rest. A Blob over an exact-size copy leaves nothing to interpret.
    const body = new Blob([new Uint8Array(bytes)], { type: 'application/octet-stream' });
    xhr.upload.onprogress = (e) => {
      if (onProgress && e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`upload failed (${xhr.status})`));
    };
    // A CORS rejection surfaces here as a status-0 "network" error with no detail —
    // say so explicitly instead of leaving a mystery in the console.
    xhr.onerror = () => reject(new Error(
      'upload blocked — the R2 bucket needs a CORS rule allowing PUT from this origin (see R2_SETUP.md §6)'
    ));
    xhr.onabort = () => reject(new Error('CANCELLED'));
    if (registerAbort) registerAbort(() => { try { xhr.abort(); } catch {} });
    xhr.send(body);
  });
}

/** 🟢 PROVE THE UPLOAD. R2 accepting the PUT only means the request was well formed —
 *  it does not tell us the object we can now read back is the size we meant to store.
 *  One HEAD closes that gap: if the stored length disagrees with the ciphertext we
 *  produced, the recipient WILL stall part-way through the download, and it is far
 *  better to fail here, loudly, with both numbers, than to ship a message that can
 *  never open. A HEAD that simply can't run (CORS, offline) is not treated as a
 *  failure — we only act on a definite mismatch. */
async function verifyUpload(publicUrl, expectedBytes) {
  try {
    const res = await fetch(publicUrl, { method: 'HEAD', cache: 'no-store' });
    if (!res.ok) { console.warn(`[media] verify: HEAD ${res.status} for ${publicUrl}`); return; }
    const len = Number(res.headers.get('content-length'));
    if (!Number.isFinite(len) || len <= 0) return; // header not exposed → nothing to check
    if (len !== expectedBytes) {
      throw new Error(
        `upload verify FAILED — stored ${len} bytes but the ciphertext is ${expectedBytes}. ` +
        `The recipient would stall mid-download. Not sending.`
      );
    }
    console.log(`[media] verify OK — ${expectedBytes} bytes stored`);
  } catch (e) {
    if (String(e?.message || '').startsWith('upload verify FAILED')) throw e;
    console.warn('[media] verify skipped:', e?.message);
  }
}

async function uploadCipher(cipher, onProgress, registerAbort) {
  const { uploadUrl, publicUrl } = await presign();
  console.log(`[media] uploading ${cipher.length} bytes of ciphertext → ${publicUrl}`);
  await putBytes(uploadUrl, cipher, onProgress, registerAbort);
  await verifyUpload(publicUrl, cipher.length);
  return publicUrl;
}

/** 🟢 One encrypted upload, for callers that aren't the image/video/album pipeline
 *  (voice memos today, documents next). Same secretbox + presign + PUT + verify as a
 *  photo, returning the key so the caller can seal it under its own field name. */
export async function uploadEncryptedBytes(bytes, onProgress, registerAbort) {
  const { cipher, k, n } = encryptBytes(bytes);
  const url = await uploadCipher(cipher, onProgress, registerAbort);
  return { url, k, n };
}

// ── 3. seal the media key JSON per-device (byte-identical to CryptoService) ──
//    sealImageKey* / sealVideoKey* / sealBatchKey* all just call sealText* and rename
//    the field, so one helper covers all three.
async function sealMediaKey(payloadJson, participants) {
  const sealed = await sealForUsers(payloadJson, participants || []);
  if (!sealed || !Object.keys(sealed.perDeviceKeys || {}).length) return null;
  return { map: sealed.perDeviceKeys, senderDeviceId: sealed.senderDeviceId };
}

// ── helpers ──
export function newTempId() {
  return `web_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function previewFor(items) {
  if (items.length === 1) return items[0].kind === 'video' ? '🎥 Video' : '📷 Photo';
  const photos = items.filter((i) => i.kind !== 'video').length;
  const videos = items.length - photos;
  if (photos && !videos) return `${photos} Photos`;
  if (videos && !photos) return `${videos} ${videos === 1 ? 'Video' : 'Videos'}`;
  return `${photos} ${photos === 1 ? 'Photo' : 'Photos'}, ${videos} ${videos === 1 ? 'Video' : 'Videos'}`;
}

/** 🟥 The phone's chat-doc write, reproduced field for field (ChatModal ~21610).
 *  ChatsTab sorts AND renders by `timestamp` and derives the row's ticks from
 *  lastMessageId + lastMessageSent/Delivered/Seen — omit them and the row keeps
 *  describing the PREVIOUS app-sent message. */
export async function writeChatDoc({ chat, myUid, myName, messageId, preview }) {
  const chatRef = doc(db, 'chats', chat.id);
  const base = {
    lastMessage: preview,
    timestamp: serverTimestamp(),
    lastMessageSystem: deleteField(),
    lastMessageSenderId: myUid,
    lastMessageSent: true,
    lastMessageDelivered: false,
    lastMessageSeen: false,
    lastMessageId: messageId,
  };

  if (chat.isGroup) {
    let counts = {};
    try {
      const snap = await getDoc(chatRef);
      counts = { ...(snap.data()?.unreadCounts || {}) };
    } catch {}
    (chat.participants || []).forEach((uid) => {
      if (!uid) return;
      counts[uid] = uid === myUid ? 0 : (counts[uid] || 0) + 1;
    });
    await setDoc(chatRef, { ...base, unreadCounts: counts }, { merge: true }).catch(() => {});
    return;
  }

  const other = (chat.participants || []).find((u) => u && u !== myUid);
  const update = {
    ...base,
    participants: chat.participants,
    unreadCounts: { ...(other ? { [other]: increment(1) } : {}), [myUid]: 0 },
    deletedFor: {
      [myUid]: deleteField(),
      ...(other ? { [other]: deleteField() } : {}),
    },
  };
  if (other && myName) {
    update.participantNames = {
      ...(chat.participantNames || {}),
      [myUid]: myName,
    };
  }
  await setDoc(chatRef, update, { merge: true }).catch(() => {});
}

/**
 * Send one or many photos/videos.
 *
 * @param {object}   a.chat        the chat doc ({id, participants, isGroup, …})
 * @param {string}   a.myUid
 * @param {string}   a.myName      display name (groups stamp senderName on the doc)
 * @param {Array}    a.items       prepared items from mediaTools.prepareFile()
 * @param {string}   a.caption
 * @param {string}   a.tempId      clientTempId — the optimistic bubble's key
 * @param {function} a.onProgress  (0..1) overall
 * @param {function} a.registerAbort  gets a cancel() for the in-flight PUT
 * @returns {Promise<string>} the Firestore message id
 */
export async function sendMediaMessage({
  chat, myUid, myName, items, caption = '', tempId, onProgress, registerAbort, reply,
  mentions, mentionsEveryone,
}) {
  /* 🟥 THE WEB'S CAPTION FIELD *IS* THE COMPOSER — same textarea, different
     placeholder — so a caption here is typed into the one field that already
     resolves an "@". It reached the picker, turned blue while being typed, and then
     went out as a photo that named nobody, because the send path was not told. */
  const _named = Array.isArray(mentions) ? mentions : [];
  const _namedAll = !!mentionsEveryone;
  const _mentionFields = {
    ...(_named.length ? { mentions: _named } : {}),
    ...(_namedAll ? { mentionsEveryone: true } : {}),
  };
  if (!chat?.id || !items?.length) throw new Error('nothing to send');
  const participants = sealMembers(chat);   // 🔐 active members only
  const clientTempId = tempId || newTempId();
  const isGroup = !!chat.isGroup;

  // ── upload every item, reporting ONE combined percentage ──
  const per = new Array(items.length).fill(0);
  const bump = () => onProgress?.(per.reduce((a, b) => a + b, 0) / items.length);

  const uploaded = [];
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    const { cipher, k, n } = encryptBytes(it.bytes);
    // eslint-disable-next-line no-await-in-loop
    const url = await uploadCipher(cipher, (f) => { per[i] = f; bump(); }, registerAbort);
    per[i] = 1; bump();
    uploaded.push({ url, key: { k, n, type: it.kind }, item: it });
  }

  const messagesRef = collection(db, 'chats', chat.id, 'messages');
  const msgRef = doc(messagesRef);
  const messageId = msgRef.id;
  const preview = previewFor(items);

  // ── single image / single video ──
  if (uploaded.length === 1) {
    const { url, key, item } = uploaded[0];
    const isVideo = item.kind === 'video';
    const sealed = await sealMediaKey(JSON.stringify({ k: key.k, n: key.n }), participants);
    /* 🔒 the file went up ENCRYPTED — without a sealed key nobody (the phone included) can ever
       open it, and a key-less copy reads as plaintext. Fail the send instead of shipping it. */
    if (!sealed || !sealed.map || !Object.keys(sealed.map).length) throw new Error('SEAL_FAILED');
    /* 🔐 the caption and the thumbnail under the photo's own key (../crypto/mediaMeta) — the
       document and the relay carry the box, never the words or a picture of the picture */
    const _meta = sealMediaMeta(key.k, { c: (caption || '').trim(), [isVideo ? 'v' : 't']: item.thumbB64 || undefined });

    const data = {
      type: isVideo ? 'video' : 'image',
      clientTempId,
      [isVideo ? 'videoUrl' : 'imageUrl']: url,
      ...(sealed && {
        [isVideo ? 'videoPerDeviceKeys' : 'imagePerDeviceKeys']: sealed.map,
        senderDeviceId: sealed.senderDeviceId,
      }),
      e2eeMedia: true, ...(isVideo ? {} : { e2eeImage: true }),   // 🔒 once encrypted, always known to be
      caption: (caption || '').trim(),
      filterColor: null,
      ...(item.thumbB64 && {
        [isVideo ? 'videoThumbnailBase64' : 'imageThumbnailBase64']: item.thumbB64,
      }),
      [isVideo ? 'videoQuality' : 'imageQuality']: item.quality || 'hd',
      ...(isVideo && item.durationMs ? { duration: Math.round(item.durationMs / 1000) } : {}),
      /* 📶 the size, as a document carries its own: a phone whose Storage & Data does not
         auto-download this on its network shows it on the download button */
      ...(item.bytes && item.bytes.byteLength ? { mediaBytes: item.bytes.byteLength } : {}),
      senderId: myUid,
      isSticker: false,
      timestamp: serverTimestamp(),
      width: item.width || null,
      height: item.height || null,
      sent: true,
      delivered: false,
      seen: false,
      ...(isGroup ? { senderName: myName || 'Web', isGroup: true } : {}),
      ..._mentionFields,
      ...(reply || {}),
    };
    await setMessageDoc(msgRef, withSealedMeta(data, _meta));

    relayNewMessage({
      chatId: chat.id, messageId, fromUserId: myUid, participants, isGroup,
      cloudText: preview, clientMsgId: clientTempId,
      mentions: _named, mentionsEveryone: _namedAll,
      extra: withSealedMeta({
        type: isVideo ? 'video' : 'image',
        width: item.width, height: item.height,
        [isVideo ? 'videoUrl' : 'imageUrl']: url,
        ...(sealed && {
          [isVideo ? 'videoPerDeviceKeys' : 'imagePerDeviceKeys']: sealed.map,
          senderDeviceId: sealed.senderDeviceId,
        }),
        e2eeMedia: true, ...(isVideo ? {} : { e2eeImage: true }),
        ...(item.thumbB64 && {
          [isVideo ? 'videoThumbnailBase64' : 'imageThumbnailBase64']: item.thumbB64,
        }),
        [isVideo ? 'videoQuality' : 'imageQuality']: item.quality || 'hd',
        ...(item.bytes && item.bytes.byteLength ? { mediaBytes: item.bytes.byteLength } : {}),
        caption: (caption || '').trim(),
        filterColor: null,
        isSticker: false,
        ...(isGroup ? { senderName: myName || 'Web' } : {}),
        ...(reply || {}),
      }, _meta),
    });

    await writeChatDoc({ chat, myUid, myName, messageId, preview });
    return messageId;
  }

  // ── album (type:'images') — ONE sealed payload holding EVERY item's key ──
  const keysPayload = JSON.stringify(uploaded.map((u) => u.key)); // [{k,n,type}, …]
  const sealed = await sealMediaKey(keysPayload, participants);
  if (!sealed || !sealed.map || !Object.keys(sealed.map).length) throw new Error('SEAL_FAILED');   // 🔒 see the single send above
  const images = uploaded.map((u) => u.url);
  const batchThumbnails = uploaded.map((u) => u.item.thumbB64 || null);
  const mediaQualities = uploaded.map((u) => u.item.quality || 'hd');
  /* 🔐 the caption and every tile's thumbnail under the album's own key; the relay, the first four */
  const _k0 = uploaded[0]?.key?.k;
  const _meta = sealMediaMeta(_k0, { c: (caption || '').trim(), b: batchThumbnails });
  const _relayMeta = sealMediaMeta(_k0, { c: (caption || '').trim(), b: batchThumbnails.slice(0, 4) });

  await setMessageDoc(msgRef, withSealedMeta({
    type: 'images',
    clientTempId,
    filterColor: null,
    images,
    batchThumbnails,
    mediaQualities,
    ...(sealed && { batchPerDeviceKeys: sealed.map, senderDeviceId: sealed.senderDeviceId }),
    e2eeMedia: true,
    senderId: myUid,
    caption: (caption || '').trim(),
    timestamp: serverTimestamp(),
    sent: true,
    delivered: false,
    seen: false,
    ...(isGroup ? { senderName: myName || 'Web', isGroup: true } : {}),
    ..._mentionFields,
    ...(reply || {}),
  }, _meta));

  relayNewMessage({
    chatId: chat.id, messageId, fromUserId: myUid, participants, isGroup,
    cloudText: preview, clientMsgId: clientTempId,
    mentions: _named, mentionsEveryone: _namedAll,
    extra: withSealedMeta({
      type: 'images',
      images,
      caption: (caption || '').trim(),
      filterColor: null,
      ...(sealed && { batchPerDeviceKeys: sealed.map, senderDeviceId: sealed.senderDeviceId }),
      e2eeMedia: true,
      mediaQualities,
      // the phone slices to 4 so an FCM data payload can't blow its size cap
      batchThumbnails: batchThumbnails.slice(0, 4),
      ...(isGroup ? { senderName: myName || 'Web' } : {}),
      ...(reply || {}),
    }, _relayMeta),
  });

  await writeChatDoc({ chat, myUid, myName, messageId, preview });
  return messageId;
}
